import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MobileStickyBar from '@/components/feature/MobileStickyBar';
import HeroSection from './components/HeroSection';
import ServicesOverview from './components/ServicesOverview';
import WhyChooseUs from './components/WhyChooseUs';
import ParallaxSection from './components/ParallaxSection';
import GalleryPreview from './components/GalleryPreview';
import ProcessSection from './components/ProcessSection';
import ServiceAreasPreview from './components/ServiceAreasPreview';
import ReviewsPreview from './components/ReviewsPreview';
import FAQSection from './components/FAQSection';
import CTASection from './components/CTASection';
import KeywordsBar from './components/KeywordsBar';
import useSEO from '@/hooks/useSEO';

const HomePage = () => {
  useSEO({
    title: 'Shower Doors Las Vegas | Frameless Glass, Railings & Mirrors | Quinolux',
    description: 'Quinolux Glass & Mirrors — Las Vegas #1 custom glass company. Frameless shower doors, glass railings, custom mirrors, tub enclosures & glass installation. Serving Las Vegas, Henderson, Summerlin & all of Clark County. Call (702) 604-2093 for a free estimate.',
    keywords: 'shower doors Las Vegas, frameless shower doors Las Vegas, glass railings Las Vegas, custom mirrors Las Vegas, glass company Las Vegas, shower glass Las Vegas, tub enclosures Las Vegas, sliding shower doors Las Vegas, glass installation Las Vegas, mirror installation Las Vegas',
    canonical: 'https://quinoluxglass.com/',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/#localbusiness`,
        name: 'Quinolux Glass & Mirrors',
        description: 'Custom glass installation in Las Vegas — frameless shower doors, glass railings, custom mirrors, tub enclosures, and glass partitions serving Las Vegas, Henderson, Summerlin, and all of Clark County.',
        url: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}`,
        telephone: '+17026042093',
        email: 'info@quinoluxglass.com',
        foundingDate: '2009',
        priceRange: '$$',
        image: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/og-image.jpg`,
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Las Vegas',
          addressRegion: 'NV',
          addressCountry: 'US',
          postalCode: '89101',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 36.1699,
          longitude: -115.1398,
        },
        areaServed: [
          { '@type': 'City', name: 'Las Vegas', containedInPlace: { '@type': 'State', name: 'Nevada' } },
          { '@type': 'City', name: 'Henderson', containedInPlace: { '@type': 'State', name: 'Nevada' } },
          { '@type': 'City', name: 'Summerlin', containedInPlace: { '@type': 'State', name: 'Nevada' } },
          { '@type': 'City', name: 'North Las Vegas', containedInPlace: { '@type': 'State', name: 'Nevada' } },
          { '@type': 'City', name: 'Spring Valley', containedInPlace: { '@type': 'State', name: 'Nevada' } },
          { '@type': 'City', name: 'Paradise', containedInPlace: { '@type': 'State', name: 'Nevada' } },
          { '@type': 'City', name: 'Boulder City', containedInPlace: { '@type': 'State', name: 'Nevada' } },
        ],
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '08:00',
            closes: '18:00',
          },
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: 'Saturday',
            opens: '09:00',
            closes: '16:00',
          },
        ],
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '5.0',
          reviewCount: '500',
          bestRating: '5',
          worstRating: '1',
        },
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Glass Installation Services',
          itemListElement: [
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Frameless Shower Doors Las Vegas', description: 'Custom frameless glass shower door installation in Las Vegas.' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Sliding Glass Shower Doors Las Vegas', description: 'Space-saving sliding glass shower door installation.' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Glass Railings Las Vegas', description: 'Frameless glass railing installation for stairs and balconies.' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Custom Mirrors Las Vegas', description: 'Custom mirror fabrication and installation.' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Tub Enclosures Las Vegas', description: 'Custom glass tub enclosure installation.' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Glass Partitions Las Vegas', description: 'Office and home glass partition installation.' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Window Glass Replacement Las Vegas', description: 'Residential and commercial window glass replacement.' } },
          ],
        },
        sameAs: [
          'https://www.google.com/maps/search/Quinolux+Glass+Las+Vegas',
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/#webpage`,
        name: 'Shower Doors Las Vegas | Frameless Glass, Railings & Mirrors | Quinolux',
        description: 'Quinolux Glass & Mirrors — Las Vegas #1 custom glass company. Frameless shower doors, glass railings, custom mirrors, tub enclosures & glass installation.',
        url: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/`,
        isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/#website` },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/` },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How much do frameless shower doors cost in Las Vegas?',
            acceptedAnswer: { '@type': 'Answer', text: 'Frameless shower doors in Las Vegas typically cost between $800 and $2,500 installed, depending on size, glass thickness, and hardware finish. Call (702) 604-2093 for a free estimate.' },
          },
          {
            '@type': 'Question',
            name: 'What is the best glass company in Las Vegas for shower doors?',
            acceptedAnswer: { '@type': 'Answer', text: 'Quinolux Glass & Mirrors is Las Vegas\'s top-rated custom glass company with 500+ five-star reviews, specializing in frameless shower doors, glass railings, custom mirrors, and tub enclosures.' },
          },
          {
            '@type': 'Question',
            name: 'Do you install glass railings in Henderson and Summerlin?',
            acceptedAnswer: { '@type': 'Answer', text: 'Yes! We install glass railings throughout the entire Las Vegas Valley including Henderson, Summerlin, North Las Vegas, Spring Valley, and Paradise.' },
          },
          {
            '@type': 'Question',
            name: 'Do you offer shower door replacement in Las Vegas?',
            acceptedAnswer: { '@type': 'Answer', text: 'Yes, we handle shower door replacement for all types — frameless, sliding, and framed. Call (702) 604-2093 for a free estimate.' },
          },
          {
            '@type': 'Question',
            name: 'How long does glass installation take in Las Vegas?',
            acceptedAnswer: { '@type': 'Answer', text: 'Most installations are completed in a single day. The process includes a free measurement appointment, custom fabrication (3–5 business days), and professional installation (1–4 hours).' },
          },
        ],
      },
    ],
  });

  return (
    <main>
      <Navbar />
      <HeroSection />
      <ServicesOverview />
      <ParallaxSection />
      <WhyChooseUs />
      <GalleryPreview />
      <ProcessSection />
      <ServiceAreasPreview />
      <ReviewsPreview />
      <FAQSection />
      <CTASection />
      <KeywordsBar />
      <Footer />
      <MobileStickyBar />
    </main>
  );
};

export default HomePage;
